// ChatbotWidget.jsx
import React from "react";
import realBotImage from "./female.png";
import animatedBotGif from "./video2.gif";
import logo from "./logo.png";
import "./ChatPopup.scss";

const BACKEND_URL = "https://voicebot.outworx.com/api";
const WAKE_WORDS = ["parky", "hey parky", "hello", "hi parky"];

class ChatbotWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      awake: false,
      listening: false,
      speaking: false,
      conversation: [],
      currentLang: "en-US",
      isProcessing: false,
      message: "",
      typingDone: false,
    };

    this.recognition = null;
    this.silenceTimer = null;
    this.sessionId = crypto.randomUUID();
    this.conversationRef = React.createRef();
    this.streamingUserText = "";
    this._voicesLoaded = false;
    this._preferredVoiceName =
      localStorage.getItem("preferredFemaleVoice") || null;
    this._firstUserGestureDone = false;
  }

  async componentDidMount() {
    await this.preloadVoices();
    this.initSpeechRecognition();
    this.introTyping();

    const gestureHandler = () => {
      this._firstUserGestureDone = true;
      this.preloadVoices();
      window.removeEventListener("pointerdown", gestureHandler);
      window.removeEventListener("touchstart", gestureHandler);
    };
    window.addEventListener("pointerdown", gestureHandler);
    window.addEventListener("touchstart", gestureHandler);
  }

  introTyping() {
    const introMsg = "  ";
    let i = 0;
    const interval = setInterval(() => {
      if (i < introMsg.length) {
        this.setState((p) => ({ message: p.message + introMsg[i] }));
        i++;
      } else clearInterval(interval);
    }, 45);
  }

  // 🎙️ SpeechRecognition setup
  initSpeechRecognition() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      console.warn("SpeechRecognition not supported in this browser.");
      return;
    }

    this.recognition = new SR();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = this.state.currentLang;

    this.recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) finalTranscript += result[0].transcript;
        else interimTranscript += result[0].transcript;
      }

      const finalText = finalTranscript.trim();
      const interimText = interimTranscript.trim();

      this.updateStreamingUser(interimText || finalText);

      const finalLower = finalText.toLowerCase();

      // Wake word detection
      if (!this.state.awake && WAKE_WORDS.some((w) => finalLower.includes(w))) {
        this.wakeUpAssistant();
        this.streamingUserText = "";
        return;
      }

      // Reset silence timer on *any* user speech activity
      if (this.state.awake) {
        this.resetSilenceTimer();
        if (finalText) {
          this.handleQuery(finalText);
          this.streamingUserText = "";
        }
      }
    };

    this.recognition.onerror = (e) => {
      console.warn("SpeechRecognition error:", e);
      this.setState({ listening: false });
    };

    this.recognition.onend = () => {
      if (this.state.awake && !this.state.speaking) {
        try {
          this.recognition.start();
          this.setState({ listening: true });
        } catch (e) {
          console.warn("Restart error:", e);
        }
      }
    };
  }

  updateStreamingUser(text) {
    if (text == null) return;
    const conv = [...this.state.conversation];
    if (conv.length === 0 || conv[0].type !== "user" || conv[0].finalized) {
      const userEntry = { type: "user", text: text, finalized: false };
      const assistant = conv.find((m) => m.type === "assistant");
      this.setState({
        conversation: assistant ? [userEntry, assistant] : [userEntry],
      });
      return;
    }
    conv[0].text = text;
    this.setState({ conversation: conv });
  }

  finalizeUserMessage() {
    const conv = [...this.state.conversation];
    if (conv.length && conv[0].type === "user") conv[0].finalized = true;
    this.setState({ conversation: conv });
  }

  // 🔊 Preload voices
  async preloadVoices() {
    const loadVoices = () =>
      new Promise((resolve) => {
        const v = window.speechSynthesis.getVoices();
        if (v.length) resolve(v);
        else {
          window.speechSynthesis.onvoiceschanged = () => {
            const vv = window.speechSynthesis.getVoices();
            resolve(vv);
          };
        }
      });

    try {
      const voices = await loadVoices();
      const preferred = [
        "Google US English",
        "Microsoft Zira Desktop - English (United States)",
        "Microsoft Aria Online (Natural) - English (United States)",
        "Samantha",
        "Jenny",
      ];
      const voice =
        voices.find((v) => preferred.includes(v.name)) ||
        voices.find((v) => v.lang && v.lang.startsWith("en")) ||
        voices[0];
      if (voice) {
        this._preferredVoiceName = voice.name;
        localStorage.setItem("preferredFemaleVoice", voice.name);
      }
      this._voicesLoaded = true;
    } catch (err) {
      console.warn("Failed to preload voices:", err);
      this._voicesLoaded = false;
    }
  }

  // 🟢 Wake
  wakeUpAssistant = () => {
    if (this.state.awake) return;
    this.setState({ awake: true }, () => {
      if (!this._voicesLoaded && this._firstUserGestureDone)
        this.preloadVoices();
      this.speak("Hi! I'm Parky. How can I help you today?", () => {
        this.startListening();
        this.startSilenceTimer();
      });
    });
  };

  // 🔴 Sleep
  sleepAssistant = () => {
    this.setState({ awake: false, listening: false });
    this.stopListening();
    this.speak("Going to sleep now.");
    this.clearSilenceTimer();
  };

  // 🎧 Mic control
  startListening() {
    if (!this.recognition) return;
    try {
      this.recognition.start();
      this.setState({ listening: true });
      console.log("🎤 Listening started");
    } catch (e) {
      console.warn("Recognition start failed:", e);
    }
  }

  stopListening() {
    if (this.recognition) {
      try {
        this.recognition.stop();
        this.setState({ listening: false });
      } catch {}
    }
  }

  // 🕒 Silence timer
  startSilenceTimer() {
    this.clearSilenceTimer();
    this.silenceTimer = setTimeout(() => {
      console.log("😴 1 minute silence — sleeping...");
      this.sleepAssistant();
    }, 60000); // 1 minute
  }

  resetSilenceTimer() {
    this.startSilenceTimer();
  }

  clearSilenceTimer() {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
  }

  // 🧠 Handle user query
  async handleQuery(question) {
    if (!question.trim() || this.state.isProcessing) return;

    this.finalizeUserMessage();
    this.setState({
      conversation: [
        { type: "user", text: question, finalized: true },
        { type: "assistant", text: "" },
      ],
      isProcessing: true,
    });
    this.stopListening();

    const payload = {
      question,
      session_id: this.sessionId,
      meta: { garage: this.props?.garageData?.id || null },
      lang: this.state.currentLang,
    };

    try {
      const res = await fetch(`${BACKEND_URL}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      const fullReply = data.text?.trim() || "Sorry, I didn't understand that.";

      this.speak(fullReply, () => {
        this.setState({ speaking: false });
      });

      this.streamResponseTyping(fullReply, () => {
        this.setState({ isProcessing: false });
        if (this.state.awake && !this.state.speaking) {
          try {
            if (this.recognition) this.recognition.start();
            this.setState({ listening: true });
          } catch {}
        }
        if (this.state.awake) this.startSilenceTimer();
      });
    } catch (err) {
      console.error("Backend error:", err);
      this.speak("Server error. Please try again later.");
      this.setState({ isProcessing: false });
    }
  }

  // ⚡ Fake streaming response
  streamResponseTyping(text, onComplete) {
    const threshold = 120;
    if (text.length <= threshold) {
      let i = 0;
      const step = () => {
        i++;
        const partial = text.substring(0, i);
        this.setState((prev) => {
          const conv = [...prev.conversation];
          const assistant = conv.find((m) => m.type === "assistant");
          if (assistant) assistant.text = partial;
          else conv.push({ type: "assistant", text: partial });
          return { conversation: conv };
        });
        if (i < text.length) {
          setTimeout(step, 18 + Math.random() * 12);
        } else {
          if (onComplete) onComplete();
        }
      };
      step();
      return;
    }

    const words = text.split(" ");
    let index = 0;
    let buffer = "";

    const pushChunk = () => {
      const chunkSize = Math.floor(Math.random() * 3) + 3;
      const nextWords = words.slice(index, index + chunkSize).join(" ");
      if (!nextWords) {
        if (onComplete) onComplete();
        return;
      }

      buffer += (buffer ? " " : "") + nextWords;
      index += chunkSize;

      this.setState((prev) => {
        const conv = [...prev.conversation];
        const last = conv[conv.length - 1];
        if (!last || last.type !== "assistant") {
          if (conv.length === 0) conv.push({ type: "assistant", text: buffer });
          else conv[1] = { type: "assistant", text: buffer };
        } else {
          last.text = buffer;
        }
        return { conversation: conv };
      });

      const nextDelay = 60 + Math.random() * 140;
      setTimeout(pushChunk, nextDelay);
    };

    pushChunk();
  }

  // 🗣️ TTS
  speak(text, callback) {
    if (!window.speechSynthesis) {
      if (callback) callback();
      return;
    }

    try {
      window.speechSynthesis.cancel();
    } catch (e) {}
    this.stopListening();

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = this.state.currentLang;
    utter.pitch = 1;
    utter.rate = 1;

    const voices = window.speechSynthesis.getVoices();
    const preferred =
      this._preferredVoiceName ||
      localStorage.getItem("preferredFemaleVoice");
    if (preferred) {
      const match = voices.find((v) => v.name === preferred);
      if (match) utter.voice = match;
    } else if (voices.length) {
      const fallback =
        voices.find((v) =>
          /female|woman|girl|Samantha|Jenny|Zira|Aria/i.test(v.name)
        ) ||
        voices.find((v) => v.lang && v.lang.startsWith("en")) ||
        voices[0];
      if (fallback) utter.voice = fallback;
    }

    utter.onstart = () => {
      this.setState({ speaking: true, listening: false });
      this.stopListening();
      this.resetSilenceTimer(); // speaking counts as activity
    };

    utter.onend = () => {
      this.setState({ speaking: false }, () => {
        if (this.state.awake) {
          try {
            this.recognition.start();
            this.setState({ listening: true });
            console.log("🎤 Mic restarted after TTS");
          } catch (e) {
            console.warn("Mic restart failed:", e);
          }
          this.resetSilenceTimer(); // restart silence timer after TTS
        }
        if (callback) callback();
      });
    };

    try {
      window.speechSynthesis.speak(utter);
    } catch (err) {
      console.warn("speechSynthesis.speak failed:", err);
      setTimeout(() => {
        if (callback) callback();
      }, 300);
    }
  }

  setLanguage(lang) {
    this.setState({ currentLang: lang }, () => {
      localStorage.setItem("preferredLang", lang);
      if (this.recognition) {
        try {
          this.recognition.stop();
          this.recognition.lang = lang;
          if (this.state.awake) this.recognition.start();
        } catch {}
      }
    });
  }

  componentWillUnmount() {
    this.clearSilenceTimer();
    this.stopListening();
  }

  render() {
    const { awake, speaking, listening, conversation, message } = this.state;

    return (
      <div className="container">
        <img src={logo} alt="Logo" className="top-left-logo" />
        <div className="header">
          <h1>🅿️ Parky</h1>
          <p>Your Smart Parking Assistant</p>
        </div>

        <div
          className={`wake-orb ${
            awake ? (speaking ? "speaking" : "awake") : "sleep"
          }`}
          onClick={() =>
            awake ? this.sleepAssistant() : this.wakeUpAssistant()
          }
          title={awake ? "Tap to sleep" : "Tap to wake Parky"}
        ></div>

        <div className="main-content portrait-layout">
          <div className="character-section">
            <img
              src={speaking ? animatedBotGif : realBotImage}
              alt="AI Assistant"
              className="character-image"
            />
            <div className="status-indicator">
              <div className="test-dot">
                <div
                  className={`status-dot ${
                    speaking ? "speaking" : awake ? "listening" : ""
                  }`}
                ></div>
                <span>
                  {awake
                    ? listening
                      ? "Listening..."
                      : "Processing..."
                    : "Say 'Hey Parky' or tap the orb"}
                </span>
              </div>

              <div className="language-buttons">
                <p className="lang-title">🌐 Choose Language</p>
                <div className="lang-btn-row">
                  <button
                    className={`lang-btn ${
                      this.state.currentLang === "hi-IN" ? "active" : ""
                    }`}
                    onClick={() => this.setLanguage("hi-IN")}
                  >
                    Hindi
                  </button>
                  <button
                    className={`lang-btn ${
                      this.state.currentLang === "en-US" ? "active" : ""
                    }`}
                    onClick={() => this.setLanguage("en-US")}
                  >
                    English
                  </button>
                  <button
                    className={`lang-btn ${
                      this.state.currentLang === "es-ES" ? "active" : ""
                    }`}
                    onClick={() => this.setLanguage("es-ES")}
                  >
                    Spanish
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="conversation-section">
            <div className="conversation-header">
              <h3>💬 Conversation History</h3>
            </div>
            <div className="conversation-log" ref={this.conversationRef}>
              {conversation.map((msg, i) => (
                <div key={i} className={`message ${msg.type}-message`}>
                  <div className="message-label">
                    {msg.type === "user" ? "User:" : "Parky:"}
                  </div>
                  <div className="message-text">{msg.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!awake && (
          <div className="intro-text">
            <p>{message}</p>
          </div>
        )}
      </div>
    );
  }
}

export default ChatbotWidget;
